import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import PixelImage from "@/components/PixelImage";
import { IMAGES } from "@/data/site";

const lineReveal = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: "0%",
        transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.14 },
    }),
};

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 + i * 0.15 },
    }),
};

export default function Hero({ onPlayReel }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.28]);
    const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] overflow-hidden">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <PixelImage
                    src={IMAGES.hero}
                    alt="EXODUS FX cinematic still"
                    fit="cover"
                    duration={1900}
                    maxPixel={80}
                    delay={150}
                    className="h-full w-full"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/80 via-[#050505]/35 to-[#050505]" />
            <div className="absolute inset-0 scanlines" aria-hidden="true" />

            <motion.div style={{ opacity: fade }} className="relative z-10 flex h-full flex-col items-center justify-center px-4">
                <motion.p
                    data-testid="hero-tagline-top"
                    custom={0}
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="font-mono-x text-[10px] sm:text-xs tracking-[0.4em] text-neutral-400 mb-6 sm:mb-8 text-center"
                >
                    BOUTIQUE VFX STUDIO - ATLANTA / NEW YORK / INDONESIA
                </motion.p>

                <h1
                    data-testid="hero-display-title"
                    className="font-display font-extrabold uppercase leading-[0.82] tracking-[-0.04em] text-center select-none"
                >
                    <span className="block overflow-hidden pb-1">
                        <motion.span
                            custom={0}
                            variants={lineReveal}
                            initial="hidden"
                            animate="show"
                            data-text="EXODUS"
                            className="glitch block text-[13.5vw] sm:text-[15vw] xl:text-[13vw] text-[#F5F5F7]"
                        >
                            EXODUS
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-2">
                        <motion.span
                            custom={1}
                            variants={lineReveal}
                            initial="hidden"
                            animate="show"
                            data-text="FX"
                            className="glitch text-outline block text-[13.5vw] sm:text-[15vw] xl:text-[13vw]"
                        >
                            FX
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    data-testid="hero-tagline-bottom"
                    custom={1}
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="font-mono-x text-[10px] sm:text-xs tracking-[0.35em] text-neutral-400 mt-6 sm:mt-8 text-center max-w-xl"
                >
                    EMMY & VES NOMINATED ARTISTS - SCARY MOVIE 6 / P-VALLEY / THE WOLVES ALWAYS COME AT NIGHT
                </motion.p>

                <motion.button
                    data-testid="hero-play-showreel-button"
                    custom={2}
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    onClick={onPlayReel}
                    className="group mt-10 sm:mt-14 flex items-center gap-4"
                >
                    <span className="relative flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full border border-white/25 backdrop-blur-md bg-white/5 transition-all duration-500 group-hover:border-[#FF3333] group-hover:bg-[#FF3333] group-hover:scale-110 group-hover:shadow-[0_0_40px_rgba(255,51,51,0.5)]">
                        <Play size={16} fill="currentColor" className="ml-0.5 transition-transform duration-500 group-hover:scale-125" />
                        <span className="absolute inset-0 rounded-full border border-[#FF3333]/0 transition-all duration-700 group-hover:scale-150 group-hover:border-[#FF3333]/40 group-hover:opacity-0" aria-hidden="true" />
                    </span>
                    <span className="font-mono-x text-[10px] sm:text-[11px] tracking-[0.35em] text-neutral-300 group-hover:text-white transition-colors">
                        WATCH 2025 REEL
                    </span>
                </motion.button>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2"
                aria-hidden="true"
            >
                <span className="font-mono-x text-[9px] tracking-[0.4em] text-neutral-500">SCROLL</span>
                <motion.span
                    animate={{ scaleY: [1, 0.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="h-10 w-px bg-gradient-to-b from-[#FF3333] to-transparent origin-top"
                />
            </motion.div>
        </section>
    );
}
